import { useEffect, useState } from "react";
import { X } from "lucide-react";
import AutocompleteInput from "./AutocompleteInput";
import DatePickerField from "./DatePickerField";
import { api } from "../lib/api";

const CATEGORIES = ["Servicios", "Producto", "Consultoría", "Renta", "Comisión", "Anticipo", "Sueldo", "Otros"];
const PAYMENT_METHODS = ["Transferencia", "Depósito", "Efectivo", "Crypto", "Otro"];

const empty = {
  concept: "",
  amount: "",
  date: new Date().toISOString().slice(0, 10),
  category: "Servicios",
  payment_method: "Transferencia",
  client: "",
  note: "",
  has_invoice: false,
  payment_status: "cobrado",
};

/**
 * Reusable Income form modal — matches IngresosPage exactly (fields, testids, styles).
 * Props:
 *  - open: boolean
 *  - onClose(): void
 *  - onSaved(income): void — called after successful create/update with the resulting income
 *  - editingIncome: object|null — when set, form is pre-filled for edit and PUT is used
 *  - projectId: string|null — when set, POST goes to /projects/{id}/incomes (attaches project_id + logs activity)
 *  - testidScope: string — prefix for data-testids (default "income")
 */
export default function IncomeFormModal({ open, onClose, onSaved, editingIncome = null, projectId = null, testidScope = "income" }) {
  const [form, setForm] = useState(empty);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [clientOptions, setClientOptions] = useState([]);

  useEffect(() => {
    if (!open) return;
    setError("");
    if (editingIncome) {
      setForm({
        concept: editingIncome.concept || "",
        amount: String(editingIncome.amount ?? ""),
        date: (editingIncome.date || editingIncome.created_at || "").slice(0, 10) || new Date().toISOString().slice(0, 10),
        category: editingIncome.category || "Servicios",
        payment_method: editingIncome.payment_method || "Transferencia",
        client: editingIncome.client || "",
        note: editingIncome.note || "",
        has_invoice: Boolean(editingIncome.has_invoice),
        payment_status: editingIncome.payment_status || "cobrado",
      });
    } else {
      setForm(empty);
    }
  }, [open, editingIncome]);

  useEffect(() => {
    if (!open) return;
    api.get("/clients", { withCredentials: true })
      .then((r) => setClientOptions((r.data || []).map((c) => c.name).filter(Boolean)))
      .catch(() => setClientOptions([]));
  }, [open]);

  if (!open) return null;

  const createClient = async (name) => {
    try {
      const r = await api.post("/clients", { name }, { withCredentials: true });
      setClientOptions((cur) => [...cur, r.data.name]);
      return r.data.name;
    } catch (e) {
      setError(e.response?.data?.detail || "No pudimos crear el cliente.");
      return null;
    }
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setError("");
    const amount = Number(form.amount);
    if (!form.concept.trim() || !amount || amount <= 0) {
      setError("Concepto y monto son obligatorios.");
      return;
    }
    setSaving(true);
    try {
      let response;
      if (editingIncome) {
        // PUT global (works for project incomes too since it's the same collection)
        const payload = {
          concept: form.concept,
          amount,
          date: form.date,
          category: form.category,
          payment_method: form.payment_method,
          client: form.client,
          has_invoice: form.has_invoice,
          note: form.note,
          payment_status: form.payment_status === "pendiente" ? "por_cobrar" : form.payment_status,
        };
        response = await api.put(`/incomes/${editingIncome.id}`, payload, { withCredentials: true });
      } else if (projectId) {
        // Project-scoped create → attaches project_id + logs activity
        const payload = {
          concept: form.concept,
          amount,
          date: form.date,
          category: form.category,
          payment_method: form.payment_method,
          client: form.client,
          has_invoice: form.has_invoice,
          note: form.note,
          payment_status: form.payment_status,
        };
        response = await api.post(`/projects/${projectId}/incomes`, payload, { withCredentials: true });
      } else {
        // Global create via smart-input (matches IngresosPage)
        const body = new FormData(ev.currentTarget);
        body.set("type", "income");
        body.set("note", form.concept);
        body.set("details", form.note);
        body.set("amount", String(amount));
        body.set("date", form.date);
        body.set("category", form.category);
        body.set("payment_method", form.payment_method);
        body.set("client", form.client);
        body.set("has_invoice", form.has_invoice ? "true" : "false");
        response = await api.post("/smart-input/manual-file", body, { withCredentials: true });
      }
      onSaved && onSaved(response.data);
      onClose && onClose();
    } catch (e) {
      setError(e.response?.data?.detail || "No pudimos guardar el ingreso.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="income-modal-overlay" data-testid={`${testidScope}-modal`}>
      <form className="income-modal income-modal-form" onSubmit={submit} data-testid={`${testidScope}-form`}>
        <button type="button" className="modal-close" data-testid={`${testidScope}-modal-close`} onClick={onClose} aria-label="Cerrar"><X /></button>
        <h2>{editingIncome ? "Editar Ingreso" : "Añadir Ingreso"}</h2>
        <label>Concepto
          <input data-testid={`${testidScope}-concept-input`} value={form.concept} onChange={(e) => setForm({ ...form, concept: e.target.value })} required />
        </label>
        <label>Monto
          <input data-testid={`${testidScope}-amount-input`} type="number" min="0.01" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required />
        </label>
        <label>Fecha
          <DatePickerField value={form.date} onChange={(v) => setForm({ ...form, date: v })} testId={`${testidScope}-date-input`} />
        </label>
        <label>Categoría
          <select data-testid={`${testidScope}-category-select`} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </label>
        <label>Método de pago
          <select data-testid={`${testidScope}-payment-select`} value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })}>
            {PAYMENT_METHODS.map((o) => <option key={o}>{o}</option>)}
          </select>
        </label>
        <label>Estado del cobro
          <select data-testid={`${testidScope}-status-select`} value={form.payment_status} onChange={(e) => setForm({ ...form, payment_status: e.target.value })}>
            <option value="cobrado">Cobrado</option>
            <option value="por_cobrar">Por cobrar</option>
          </select>
        </label>
        <label>Cliente / Empresa (opcional)
          <AutocompleteInput value={form.client} onChange={(v) => setForm({ ...form, client: v })} options={clientOptions} onCreate={createClient} createLabel="Crear cliente" placeholder="Ej: Cliente Alpha SA..." testId={`${testidScope}-client-input`} />
        </label>
        <label>Nota (opcional)
          <textarea data-testid={`${testidScope}-note-input`} rows="2" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
        </label>
        <label className="expenses-inline-check">
          <input data-testid={`${testidScope}-has-invoice-checkbox`} type="checkbox" checked={form.has_invoice} onChange={(e) => setForm({ ...form, has_invoice: e.target.checked })} />
          <span>Tiene factura (CFDI)</span>
        </label>
        {!editingIncome && !projectId && (
          <label>Adjuntar archivo (opcional)
            <input data-testid={`${testidScope}-file-input`} name="file" type="file" accept="application/pdf,image/*" />
          </label>
        )}
        {error && <p className="billing-error" data-testid={`${testidScope}-form-error`}>{error}</p>}
        <button type="submit" className="primary" disabled={saving} data-testid={`${testidScope}-save-button`}>
          {saving ? "Guardando…" : (editingIncome ? "Guardar cambios" : "Guardar ingreso")}
        </button>
      </form>
    </div>
  );
}
